import {
  Drawer,
  Box,
  Divider,
  ListItemText,
  Typography,
  Badge,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import LogoImage from "assets/images/Prof_Reg_OHIO.png";
import { NestedMenu } from "../NestedMenu/nestedMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faEye,
  faBell,
  faIdBadge,
  faRemove,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useAppStore } from "@/stores/appStore";
import { useApplicationsStore } from "@/stores/applicationsStore";
import { endImpersonation } from "@/services/leftNav";
import { baseURL } from "@/services/http";
import { emptyCart } from "@/services/cart";
import { useOrgDashboardStore } from "@/stores/orgDashboardStore";
import {
  getMenuItems,
  getAdminMenuItems,
  getReportBuilder,
} from "@/utils/menuItems";
import { useEmploymentRecordsStore } from "@/pages/employment/stores/employmentStore";
import { useEventManagementStore } from "@/stores/eventManagementStore";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useReportData } from "@/hooks/useReportData";

const StyledDrawer = styled(Drawer)(() => ({
  width: 72,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    width: 300,
    backgroundColor: "#002B6C",
  },
}));

const SmallBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    fontSize: "10px",
    width: "12px",
    height: "12px",
  },
}));

export const LeftNav = ({ name, opin, user }) => {
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { unreadCount, shoppingCart } = useAppStore();
  const { applicationsMenu, fetchApplicationsMenu } = useApplicationsStore();
  const { organizationProfile } = useOrgDashboardStore();
  const {
    selectedTrainingOrganization: trainingOrg,
    permissionsByTrainingOrg,
  } = useEventManagementStore();
  const { employmentRecords, init: employmentRecordsInit } =
    useEmploymentRecordsStore();
  const hasEmploymentRecords = employmentRecords.length > 0;
  const { isFeatureEnabled } = useFeatureFlags();
  const { queuedReports, reportPermissions } = useReportData();

  useEffect(() => {
    if (employmentRecordsInit) {
      fetchApplicationsMenu(!hasEmploymentRecords);
    }
  }, [fetchApplicationsMenu, hasEmploymentRecords, employmentRecordsInit]);

  const topMenuItems = [
    {
      text: "Shopping Cart",
      icon:
        shoppingCart && shoppingCart.length > 0 ? (
          <SmallBadge badgeContent={shoppingCart.length} color="error">
            <FontAwesomeIcon icon={faShoppingCart} size="lg" />
          </SmallBadge>
        ) : (
          <FontAwesomeIcon icon={faShoppingCart} size="lg" />
        ),
      navPath: `${baseURL}cart/view_cart?from=pwa`,
      target: "_blank",
      subItems: [
        {
          text: "Order History",
          icon: <FontAwesomeIcon icon={faEye} size="lg" />,
          navPath: `${baseURL}user/order_history?from=pwa`,
          target: "_blank",
          disabled: !hasEmploymentRecords,
        },
      ],
      disabled: !hasEmploymentRecords,
    },
    {
      text: "Notifications",
      icon:
        unreadCount > 0 ? (
          <SmallBadge badgeContent={unreadCount} color="error">
            <FontAwesomeIcon icon={faBell} size="lg" />
          </SmallBadge>
        ) : (
          <FontAwesomeIcon icon={faBell} size="lg" />
        ),
      navPath: "/notifications",
      disabled: !hasEmploymentRecords,
    },
  ];

  const endImpersonationEvent = async () => {
    await endImpersonation();
    window.location.replace(
      `${baseURL}user/administrate_user/${user.user_id}#admin`
    );
  };

  const logout = () => {
    if (shoppingCart && shoppingCart.length > 0) {
      setLogoutDialogOpen(true);
    } else {
      window.location = `${baseURL}user/logout?message=genericSignOut`;
    }
  };

  const handleLogoutConfirm = async () => {
    await emptyCart();
    window.location = `${baseURL}user/logout?message=signoutWithShoppingCartItems`;
  };

  let profileStyles = {
    pl: 2,
    pt: 1,
    display: "flex",
    alignItems: "center",
  };
  if (user.impersonation_original_user_id) {
    profileStyles = {
      ...profileStyles,
      backgroundColor: "red",
      opacity: 0.8,
      ":hover": {
        backgroundColor: "red",
      },
    };
  }
  return (
    <>
      <StyledDrawer
        variant="permanent"
        anchor="left"
        open={true}
        sx={{ display: { xs: "none", sm: "none", md: "block" } }}
      >
        <Box
          sx={{
            // mt: 2,
            pt: 1,
            pb: 1,
            minHeight: 64,
            backgroundColor: "white",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Image
            src={LogoImage.src}
            height={60}
            width={124}
            alt="OCCRRA"
            priority
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            color: "white",
            width: "100%",
          }}
        >
          <Box sx={{ ...profileStyles }}>
            <Box sx={{ mr: 2, ml: 1 }}>
              <FontAwesomeIcon icon={faIdBadge} size="xl" />
            </Box>
            <Box sx={{ ml: 2.75 }}>
              <ListItemText>
                <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                  {name}
                </Typography>
              </ListItemText>
              {user.impersonation_original_user_id && (
                <ListItemText>
                  <Typography sx={{ fontWeight: 700 }}>
                    {"<<Impersonating>>"}
                  </Typography>
                </ListItemText>
              )}
              <ListItemText>OPIN: {opin}</ListItemText>
              <ListItemText>CPL: {user.cpl}</ListItemText>
            </Box>
            {user.impersonation_original_user_id && (
              <Box sx={{ ml: 3 }}>
                <IconButton onClick={endImpersonationEvent}>
                  <FontAwesomeIcon icon={faRemove} />
                </IconButton>
              </Box>
            )}
            {!user.impersonation_original_user_id && (
              <Box sx={{ ml: 3 }}>
                <Tooltip
                  title="Logout"
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                >
                  <IconButton sx={{ color: "white" }} onClick={logout}>
                    <FontAwesomeIcon icon={faSignOutAlt} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        <NestedMenu
          menuItems={topMenuItems}
          colorPalette={{
            iconColor: "white",
            selectedColor: "#7EAB63",
            subItemBackgroundColor: "#4268B8",
            subItemIconColor: "white",
          }}
        />
        <Box sx={{ ml: 2, mr: 2 }}>
          <Divider sx={{ backgroundColor: "#FFFFFF" }} />
        </Box>

        <NestedMenu
          menuItems={getMenuItems(applicationsMenu, !hasEmploymentRecords)}
          colorPalette={{
            iconColor: "white",
            selectedColor: "#7EAB63",
            subItemBackgroundColor: "#4268B8",
          }}
        />
        <Box
          sx={{
            // position: "absolute",
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            color: "white",
            width: "100%",
          }}
        >
          <NestedMenu
            menuItems={getReportBuilder({
              user,
              queuedReports,
              reportPermissions,
            })}
            initialOpenSubMenus={[2]}
            colorPalette={{
              iconColor: "white",
              selectedColor: "#7EAB63",
              subItemBackgroundColor: "#4268B8",
              subItemIconColor: "white",
            }}
          />
        </Box>

        <Box sx={{ ml: 2, mr: 2 }}>
          <Divider sx={{ backgroundColor: "#FFFFFF" }} />
        </Box>
        <NestedMenu
          menuItems={getAdminMenuItems(
            user,
            organizationProfile,
            trainingOrg,
            !hasEmploymentRecords,
            isFeatureEnabled,
            permissionsByTrainingOrg
          )}
          initialOpenSubMenus={[2]}
          colorPalette={{
            iconColor: "white",
            selectedColor: "#7EAB63",
            subItemBackgroundColor: "#4268B8",
            subItemIconColor: "white",
          }}
        />
        <Box
          sx={{
            // position: "absolute",
            bottom: 20,
            left: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "left",
            color: "white",
            width: "100%",
          }}
        >
          <Box sx={{ ml: 2, mr: 2 }}>
            <Divider sx={{ backgroundColor: "#FFFFFF" }} />
          </Box>
          <Box sx={{ pl: 2 }}>
            <ListItemText>
              <Typography variant="caption" sx={{ "& a": { color: "white" } }}>
                Contact Us:{" "}
                <a href="mailto:support@occrra.org">support@occrra.org</a>
              </Typography>
            </ListItemText>
          </Box>
          <Box sx={{ pl: 2, display: "flex" }}>
            <ListItemText>
              <Typography variant="caption" sx={{ "& a": { color: "white" } }}>
                © 2023{" "}
                <a
                  href="https://occrra.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  OCCRRA
                </a>
                {"  "}
                All rights reserved.
              </Typography>
            </ListItemText>
          </Box>
        </Box>
      </StyledDrawer>
      <Dialog
        open={!!logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Are you sure?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have items in your cart, if you continue to sign out, they will
            be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleLogoutConfirm}>
            Logout
          </Button>
          <Button
            variant="outlined"
            onClick={() => setLogoutDialogOpen(false)}
            autoFocus
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
